import { Box, Button, styled, Typography } from "~/common/mui";
import parse from "html-react-parser";
import type ComponentTypes from "~/features/cms/componentTypes";
import classNames from "classnames";
import { resolveUrl } from "~/utils/url/urlResolver";

export interface CMSImageHeaderMediaDTO {
  url?: string;
  altText?: string;
}

export interface CMSImageHeaderButtonDTO {
  url?: string;
  labelText?: string;
  target?: string;
}

export interface CMSImageHeaderProps {
  type: ComponentTypes.CMSImageHeader;
  id: string;
  className?: string;
  media?: CMSImageHeaderMediaDTO;
  imageLink?: string;
  headerText?: string;
  subHeaderText?: string;
  content?: string;
  button?: CMSImageHeaderButtonDTO;
  buttonVisibility?: boolean;
  mirror?: boolean;
}

export const CMSImageHeader = ({
  media,
  imageLink,
  headerText,
  subHeaderText,
  content,
  button,
  buttonVisibility = false,
  mirror = false,
  className,
}: CMSImageHeaderProps) => {
  const imageUrl = media?.url;
  const hasText = Boolean(headerText || subHeaderText || content);
  const showButton = buttonVisibility && Boolean(button?.url) && Boolean(button?.labelText);
  const hasTextColumn = hasText || showButton;

  if (!imageUrl && !hasTextColumn) {
    return null;
  }

  const image = imageUrl ? <HeaderImage src={resolveUrl(imageUrl)} alt={media?.altText ?? ""} /> : null;

  return (
    <Root className={classNames("scha__cms", "scha__cms-image-header", className)}>
      <Column>
        <ImageContainer>
          {imageLink && !hasTextColumn ? <BannerLink href={resolveUrl(imageLink)}>{image}</BannerLink> : image}
        </ImageContainer>
      </Column>
      <TextColumn $mirror={mirror}>
        {subHeaderText ? <Subheader>{parse(subHeaderText)}</Subheader> : null}
        {headerText ? <Header>{parse(headerText)}</Header> : null}
        {content ? (
          <Typography variant="bodyMedium" component="div" className="jsp__user-generated-content">
            {parse(content)}
          </Typography>
        ) : null}
        {showButton ? (
          <ButtonContainer>
            <Button href={resolveUrl(button!.url!)} target={button?.target}>
              {button!.labelText}
            </Button>
          </ButtonContainer>
        ) : null}
      </TextColumn>
    </Root>
  );
};

// Preserve the JSP row/col-sm-6 gutters without depending on the legacy grid classes.
const Root = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  marginTop: theme.spacing(5),
  marginInline: theme.spacing(-1),
  [theme.breakpoints.up("tablet")]: {
    marginInline: theme.spacing(-1.5),
  },
  [theme.breakpoints.up("desktop-l")]: {
    marginInline: theme.spacing(-2),
  },
}));

const Column = styled(Box)(({ theme }) => ({
  flex: "0 0 100%",
  maxWidth: "100%",
  paddingInline: theme.spacing(1),
  [theme.breakpoints.up("tablet")]: {
    flexBasis: "50%",
    maxWidth: "50%",
    paddingInline: theme.spacing(1.5),
  },
  [theme.breakpoints.up("desktop-l")]: {
    paddingInline: theme.spacing(2),
  },
}));

const TextColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== "$mirror",
})<{ $mirror: boolean }>(({ $mirror }) => ({
  order: $mirror ? -1 : undefined,
}));

const ImageContainer = styled(Box)({
  display: "grid",
  gridTemplateColumns: "100%",
  gridTemplateRows: "100%",
  placeItems: "center",
});

const HeaderImage = styled("img")({
  maxWidth: "100%",
  maxHeight: "100%",
});

const Subheader = styled(Box)(({ theme }) => ({
  ...theme.typography.titleMedium,
  margin: theme.spacing(0, 0, 1.25),
}));

const Header = styled(Box)(({ theme }) => ({
  ...theme.typography.headlineMedium,
  margin: theme.spacing(0, 0, 3),
  ".scha__product-group-page__main &": {
    [theme.breakpoints.down("desktop-s")]: {
      fontWeight: "bold",
    },
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3.75),
}));

const BannerLink = styled("a")({
  display: "grid",
  placeItems: "center",
  width: "100%",
});
